import { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Accessibility } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BRAND, NAV } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { LanguagePicker } from "./LanguagePicker";

const testId = (label) => label.toLowerCase().replace(/\s+/g, "-");

const MobileMenu = ({ open, onClose }) => {
  const t = useT();
  const { NAV: nav } = useSite();
  const closeRef = useRef(null);
  // Deps: refs and the memoised onClose are stable; nothing else from scope is read.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-white flex flex-col" role="dialog" aria-modal="true" aria-label={t("Site menu")} id="mobile-menu" data-testid="mobile-menu">
          <div className="container-x flex items-center justify-between h-[84px] border-b border-slate-100">
            <img src={BRAND.logo} alt={BRAND.name} className="h-14 w-auto" />
            <button ref={closeRef} onClick={onClose} aria-label={t("Close menu")} data-testid="mobile-menu-close" className="w-11 h-11 rounded-md bg-[var(--blue-tint)] text-[var(--blue)] flex items-center justify-center"><X size={22} /></button>
          </div>
          <nav aria-label="Mobile" className="container-x flex-1 overflow-y-auto py-6">
            {nav.map((item, i) => (
              <motion.div key={item.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
                <NavLink to={item.to} end={item.to === "/"} onClick={onClose} data-testid={`mobile-nav-${testId(NAV[i].label)}`}
                  className={({ isActive }) => `block text-2xl font-semibold py-3.5 border-b border-slate-100 ${isActive ? "text-[var(--coral)]" : "text-[var(--blue)]"}`}>
                  {item.label}
                </NavLink>
              </motion.div>
            ))}
          </nav>
          <div className="container-x pb-8 pt-4 flex flex-col gap-3 border-t border-slate-100">
            <button type="button" onClick={() => { onClose(); document.querySelector('[data-testid="a11y-toggle"]')?.click(); }} className="btn btn-ghost-blue" data-testid="mobile-menu-accessibility"><Accessibility size={16} /> {t("Accessibility")}</button>
            <a href={`tel:${BRAND.phone.tel}`} className="btn btn-coral" data-testid="mobile-menu-phone"><Phone size={16} /> {BRAND.phone.number}</a>
            <Link to="/contact" onClick={onClose} className="btn btn-ghost" data-testid="mobile-menu-enquire"><Mail size={16} /> {t("Enquire now")}</Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Header = () => {
  const t = useT();
  const { NAV: nav } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  // setOpen/setScrolled are React state setters (stable identity), so [] / [pathname] are complete.
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const openA11y = () => document.querySelector('[data-testid="a11y-toggle"]')?.click();

  return (
    <header className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? "shadow-[0_6px_24px_-12px_rgba(0,0,0,0.18)]" : "border-b border-slate-100"}`} data-testid="site-header">
      <div className="max-w-[1500px] mx-auto px-4 lg:px-8 flex items-center justify-between h-[84px] lg:h-[104px] gap-3">
        <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
          <img src={BRAND.logo} alt={BRAND.name} className="h-14 lg:h-[76px] w-auto" />
        </Link>
        <nav aria-label="Main" className="hidden 2xl:flex items-center gap-0.5 me-auto ms-4" data-testid="desktop-nav">
          {nav.map((item, i) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={`nav-${testId(NAV[i].label)}`} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 lg:gap-3">
          <button type="button" onClick={openA11y} className="link-u text-sm hidden xl:inline-flex items-center gap-1.5 me-1" data-testid="header-accessibility"><Accessibility size={16} /> {t("Accessibility")}</button>
          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${BRAND.phone.tel}`} data-testid="header-phone" className="btn btn-coral !py-2.5 whitespace-nowrap"><Phone size={16} /> {BRAND.phone.number}</a>
            <Link to="/contact" className="btn btn-ghost !py-2.5 whitespace-nowrap" data-testid="header-cta-enquire"><Mail size={16} /> {t("Enquire now")}</Link>
          </div>
          <LanguagePicker />
          <button onClick={() => setOpen(true)} aria-label={t("Open menu")} aria-expanded={open} aria-controls="mobile-menu" data-testid="menu-toggle" className="2xl:hidden w-11 h-11 rounded-md bg-[var(--blue)] text-white flex items-center justify-center hover:bg-[var(--blue-dark)] transition-colors">
            <Menu size={22} />
          </button>
        </div>
      </div>
      <MobileMenu open={open} onClose={close} />
    </header>
  );
};
